import {
  collection,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  increment,
  arrayUnion,
  arrayRemove,
  setDoc,
  deleteDoc,
  addDoc,
  getCountFromServer,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { createNotifications } from './notificationService';
import { normalizeAddress, createCompositeId, ADDRESS_MENTION_REGEX, isEmailUserIdentifier } from './addressUtils';

// Helper to parse mentions from comment text
// Extracts cryptocurrency addresses (without @ prefix required)
// Returns object with mentions array (max 100 to prevent spam)
const parseMentions = (text, normalizedAuthorAddress) => {
  if (!text) return { mentions: [] };

  const mentions = [];
  let match;
  // Create a new RegExp instance to avoid state issues with global flag
  const regex = new RegExp(ADDRESS_MENTION_REGEX.source, ADDRESS_MENTION_REGEX.flags);

  while ((match = regex.exec(text)) !== null) {
    const rawAddress = match[1];

    // Normalize address based on format
    // EVM: lowercase (case-insensitive)
    // Bitcoin/Litecoin legacy: preserve case (Base58 is case-sensitive)
    // SegWit/CashAddr: already lowercase (Bech32 is lowercase)
    const normalizedMentionedAddress = normalizeAddress(rawAddress);

    // Don't allow mentioning self
    if (normalizedMentionedAddress === normalizedAuthorAddress) {
      continue;
    }

    // Don't add duplicates
    if (!mentions.includes(normalizedMentionedAddress)) {
      mentions.push(normalizedMentionedAddress);
    }
  }
  const sorted = mentions.sort();

  // Sort alphabetically so order in text doesn't matter
  return { mentions: sorted };
};

export const countMentions = (text, normalizedAuthorAddress = '') => {
  const { mentions } = parseMentions(text, normalizedAuthorAddress);
  return mentions.length;
};

// ==================== COMMENT OPERATIONS ====================

// Create a new comment for address page
export const createComment = async (targetAddress, text, userIdentifier, targetChain, parentComment = null) => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('You must be signed in to create a comment');
    }

    // Check if user is email-based (compositeId starts with 'e_')
    const isEmailUser = isEmailUserIdentifier(userIdentifier);

    // Parse mentions from comment text (auto-detect addresses)
    // For email users, pass null as userAddress to avoid self-mention detection issues
    const { mentions: mentionedAddresses } = parseMentions(text, isEmailUser ? null : userIdentifier);

    // Normalize addresses (lowercase)
    const normalizedTargetAddress = normalizeAddress(targetAddress);
    // For email users, use their compositeId as-is; for wallet users, normalize the address
    const normalizedAuthorAddress = isEmailUser ? userIdentifier : normalizeAddress(userIdentifier);

    // Create composite IDs for multi-chain support
    const targetCompositeId = createCompositeId(targetChain, targetAddress);

    // For email users, use their compositeId directly; for wallet users, create chain_address format
    const authorCompositeId = isEmailUser ? userIdentifier : createCompositeId("ethereum", userIdentifier);

    const commentData = {
      // Composite IDs (required by firestore.rules)
      // These preserve original chain context for notification links
      targetCompositeId, // e.g., 'polygon_0xalice...' or 'bitcoin_1ABC...'
      authorCompositeId, // e.g., 'ethereum_0xbob...' or 'e_xxx' for email users
      // Target type (required by firestore.rules)
      targetType: 'address', // 'address' or 'coin'
      // Normalized addresses (for queries)
      targetAddress: normalizedTargetAddress, // e.g., '0xalice...' (lowercase)
      authorAddress: normalizedAuthorAddress, // e.g., '0xbob...' or 'e_xxx' for email users
      // Original chain context (preserved for display and context)
      targetChain: targetChain, // e.g., 'polygon' (original chain where comment was created)
      authorChain: isEmailUser ? null : 'ethereum', // null for email users
      chain: targetChain, // Chain identifier (ethereum, bitcoin, bitcoincash, litecoin)
      text: text.trim(),
      mentionedAddresses, // Array of addresses mentioned in comment
      parentId: parentComment ? parentComment.id : null,
      threadRootId: parentComment ? (parentComment.threadRootId || parentComment.id) : null,
      level: parentComment ? 1 : 0,
      replyingToAddress: parentComment?.authorAddress || null,
      createdAt: serverTimestamp(),
      updatedAt: null,
      edited: false,
      editHistory: [],
      upvotes: 0,
      downvotes: 0,
      upvoters: [],
      downvoters: [],
      replyCount: 0,
      flagged: false,
      flagCount: 0,
      flaggedBy: [],
      removed: false,
      removedAt: null,
      removedBy: null,
      deleted: false,
      deletedAt: null,
      needsReassessment: true // New comments need assessment
    };

    const docRef = await addDoc(collection(db, 'comments'), commentData);

    // If this is a reply, increment thread root's reply count (for total thread count)
    if (parentComment) {
      // Use threadRootId to track total replies in the thread
      const threadRootId = commentData.threadRootId || parentComment.id;
      await updateDoc(doc(db, 'comments', threadRootId), {
        replyCount: increment(1)
      });
    }

    // Create the comment object to return
    // Use current Date for createdAt since serverTimestamp() is null until confirmed
    const createdComment = {
      id: docRef.id,
      ...commentData,
      createdAt: new Date()
    };

    // Create notifications (non-blocking, errors logged in service)
    createNotifications(createdComment, parentComment, mentionedAddresses);

    return createdComment;
  } catch (error) {
    console.error('Error creating comment:', error);
    throw error;
  }
};

// Get top-level comments for an address on a specific chain
// For EVM addresses: Returns comments from ALL EVM chains (same person)
// For non-EVM addresses: Returns only comments for that specific chain
export const getTopLevelComments = async (targetAddress, limitCount = 100) => {
  try {
    const normalizedAddress = normalizeAddress(targetAddress);

    // Build query
    // Query by targetAddress only (shares across all EVM chains)
    const constraints = [
      where('targetAddress', '==', normalizedAddress),
      where('level', '==', 0),
      where('removed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    ];

    const q = query(collection(db, 'comments'), ...constraints);

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching top-level comments:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for top-level comments on an address
 * Returns unsubscribe function to stop listening
 *
 * @param {string} targetAddress - Target address
 * @param {function} onUpdate - Callback with { added, modified, removed } arrays
 * @param {function} onError - Error callback
 * @param {number} limitCount - Max comments to fetch
 * @returns {function} Unsubscribe function
 */
export const subscribeToTopLevelComments = (targetAddress, onUpdate, onError, limitCount = 100) => {
  try {
    const normalizedAddress = normalizeAddress(targetAddress);

    const constraints = [
      where('targetAddress', '==', normalizedAddress),
      where('level', '==', 0),
      where('removed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    ];

    const q = query(collection(db, 'comments'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const changes = {
          added: [],
          modified: [],
          removed: []
        };

        snapshot.docChanges().forEach((change) => {
          const commentData = {
            id: change.doc.id,
            ...change.doc.data()
          };

          if (change.type === 'added') {
            changes.added.push(commentData);
          } else if (change.type === 'modified') {
            changes.modified.push(commentData);
          } else if (change.type === 'removed') {
            changes.removed.push(commentData);
          }
        });

        onUpdate(changes);
      },
      (error) => {
        console.error('Error in top-level comments listener:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up top-level comments listener:', error);
    if (onError) onError(error);
    return () => {}; // Return no-op unsubscribe
  }
};

// Get replies for a comment thread
export const getReplies = async (threadRootId) => {
  try {
    const q = query(
      collection(db, 'comments'),
      where('threadRootId', '==', threadRootId),
      where('level', '==', 1),
      where('removed', '==', false),
      where('deleted', '==', false),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching replies:', error);
    throw error;
  }
};

// Get comments that mention a specific address
// For EVM addresses: Returns mentions from ALL EVM chains (same person)
// For non-EVM addresses: Returns only mentions for that specific address
export const getMentionedComments = async (mentionedAddress, limitCount = 20) => {
  try {
    const normalizedAddress = normalizeAddress(mentionedAddress);

    const q = query(
      collection(db, 'comments'),
      where('mentionedAddresses', 'array-contains', normalizedAddress),
      where('removed', '==', false),
      where('deleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching mentioned comments:', error);
    throw error;
  }
};

// Get count of mentioned comments (lightweight, doesn't load full data)
// For EVM addresses: Returns count from ALL EVM chains (same person)
export const getMentionedCommentsCount = async (mentionedAddress) => {
  try {
    const normalizedAddress = normalizeAddress(mentionedAddress);

    const q = query(
      collection(db, 'comments'),
      where('mentionedAddresses', 'array-contains', normalizedAddress),
      where('removed', '==', false)
    );

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error fetching mentioned comments count:', error);
    return 0;
  }
};

// ==================== COIN COMMENT OPERATIONS ====================

/**
 * Get top-level comments for a coin
 */
export const getTopLevelCommentsForCoin = async (coinId, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'comments'),
      where('targetType', '==', 'coin'),
      where('targetCoinId', '==', coinId),
      where('level', '==', 0),
      where('removed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching coin comments:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for top-level comments on a coin
 * Returns unsubscribe function to stop listening
 *
 * @param {string} coinId - CoinGecko coin ID
 * @param {function} onUpdate - Callback with { added, modified, removed } arrays
 * @param {function} onError - Error callback
 * @param {number} limitCount - Max comments to fetch
 * @returns {function} Unsubscribe function
 */
export const subscribeToTopLevelCommentsForCoin = (coinId, onUpdate, onError, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'comments'),
      where('targetType', '==', 'coin'),
      where('targetCoinId', '==', coinId),
      where('level', '==', 0),
      where('removed', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const changes = {
          added: [],
          modified: [],
          removed: []
        };

        snapshot.docChanges().forEach((change) => {
          const commentData = {
            id: change.doc.id,
            ...change.doc.data()
          };

          if (change.type === 'added') {
            changes.added.push(commentData);
          } else if (change.type === 'modified') {
            changes.modified.push(commentData);
          } else if (change.type === 'removed') {
            changes.removed.push(commentData);
          }
        });

        onUpdate(changes);
      },
      (error) => {
        console.error('Error in coin comments listener:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up coin comments listener:', error);
    if (onError) onError(error);
    return () => {}; // Return no-op unsubscribe
  }
};

/**
 * Create a comment on a coin
 */
export const createCoinComment = async (coinId, text, userIdentifier, parentComment = null, coinName = null) => {
  try {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to create a comment');
    }

    // Check if user is email-based (compositeId starts with 'e_')
    const isEmailUser = isEmailUserIdentifier(userIdentifier);

    const { mentions: mentionedAddresses } = parseMentions(text, isEmailUser ? null : userIdentifier);
    const normalizedAuthorAddress = isEmailUser ? userIdentifier : normalizeAddress(userIdentifier);
    const authorCompositeId = isEmailUser ? userIdentifier : createCompositeId("ethereum", userIdentifier);

    const commentData = {
      // Coin-specific fields
      targetType: 'coin',
      targetCoinId: coinId,
      targetCoinName: coinName,

      // Author
      authorCompositeId,
      authorAddress: normalizedAuthorAddress,
      authorChain: isEmailUser ? null : 'ethereum',

      // Content
      text: text.trim(),
      mentionedAddresses,
      parentId: parentComment ? parentComment.id : null,
      threadRootId: parentComment ? (parentComment.threadRootId || parentComment.id) : null,
      level: parentComment ? 1 : 0,
      replyingToAddress: parentComment?.authorAddress || null,
      createdAt: serverTimestamp(),
      updatedAt: null,
      edited: false,
      editHistory: [],
      upvotes: 0,
      downvotes: 0,
      upvoters: [],
      downvoters: [],
      replyCount: 0,
      flagged: false,
      flagCount: 0,
      flaggedBy: [],
      removed: false,
      removedAt: null,
      removedBy: null,
      deleted: false,
      deletedAt: null,
      needsReassessment: true // New comments need assessment
    };

    const docRef = await addDoc(collection(db, 'comments'), commentData);

    if (parentComment) {
      const threadRootId = commentData.threadRootId || parentComment.id;
      await updateDoc(doc(db, 'comments', threadRootId), {
        replyCount: increment(1)
      });
    }

    // Use current Date for createdAt since serverTimestamp() is null until confirmed
    const createdComment = {
      id: docRef.id,
      ...commentData,
      createdAt: new Date()
    };

    // Create notifications for mentions
    createNotifications(createdComment, parentComment, mentionedAddresses);

    return createdComment;
  } catch (error) {
    console.error('Error creating coin comment:', error);
    throw error;
  }
};

// Edit a comment
export const editComment = async (commentDocId, newText, userIdentifier) => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('You must be signed in to edit a comment');
    }

    const commentRef = doc(db, 'comments', commentDocId);
    const commentSnap = await getDoc(commentRef);

    if (!commentSnap.exists()) {
      throw new Error('Comment not found');
    }

    const commentData = commentSnap.data();

    // Check if user is email-based
    const isEmailUser = isEmailUserIdentifier(userIdentifier);
    const normalizedUserIdentifier = isEmailUser ? userIdentifier : userIdentifier.toLowerCase();

    // Verify ownership
    if (commentData.authorAddress !== normalizedUserIdentifier) {
      throw new Error('Not authorized to edit this comment');
    }

    // Parse mentions from edited text (auto-detect addresses)
    const { mentions: mentionedAddresses } = parseMentions(newText, isEmailUser ? null : userIdentifier);

    // Find newly mentioned addresses (for notifications)
    const oldMentions = commentData.mentionedAddresses || [];
    const newMentions = mentionedAddresses.filter(addr => !oldMentions.includes(addr));

    // Check cooldown (10 seconds)
    if (commentData.updatedAt) {
      // Handle both Firestore Timestamp and Date objects
      let lastUpdate;
      if (commentData.updatedAt.toDate) {
        // Firestore Timestamp
        lastUpdate = commentData.updatedAt.toDate();
      } else if (commentData.updatedAt instanceof Date) {
        // Already a Date
        lastUpdate = commentData.updatedAt;
      } else {
        // Try to parse as string (legacy data)
        lastUpdate = new Date(commentData.updatedAt);
      }

      const timeSinceUpdate = Date.now() - lastUpdate.getTime();
      if (timeSinceUpdate < 10000) {
        const remainingSeconds = Math.ceil((10000 - timeSinceUpdate) / 1000);
        throw new Error(`Please wait ${remainingSeconds} more second(s) before editing again`);
      }
    }

    // Add current version to edit history
    const editHistory = commentData.editHistory || [];
    editHistory.push({
      text: commentData.text,
      editedAt: new Date()
    });

    const updateData = {
      text: newText.trim(),
      mentionedAddresses, // Update mentions when text is edited
      updatedAt: serverTimestamp(),
      edited: true,
      editHistory,
      needsReassessment: true // Edited comments need re-assessment
    };

    await updateDoc(commentRef, updateData);

    // Create notifications for newly mentioned addresses
    if (newMentions.length > 0) {
      // Create comment object for notification
      const commentForNotification = {
        id: commentDocId,
        authorAddress: commentData.authorAddress,
        authorCompositeId: commentData.authorCompositeId, // Required for actorUid in notifications
        text: newText.trim(),
        targetAddress: commentData.targetAddress,
        chain: commentData.chain,
        level: commentData.level
      };

      // Create notifications for new mentions (non-critical, won't throw)
      // Using await here is safe because createNotifications has internal error handling
      await createNotifications(commentForNotification, null, newMentions);
    }

    return true;
  } catch (error) {
    console.error('Error editing comment:', error);
    throw error;
  }
};

// Soft delete a comment
export const deleteComment = async (commentDocId, userIdentifier) => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete a comment');
    }

    const commentRef = doc(db, 'comments', commentDocId);
    const commentSnap = await getDoc(commentRef);

    if (!commentSnap.exists()) {
      throw new Error('Comment not found');
    }

    const commentData = commentSnap.data();

    // Check if user is email-based
    const isEmailUser = isEmailUserIdentifier(userIdentifier);
    const normalizedUserIdentifier = isEmailUser ? userIdentifier : userIdentifier.toLowerCase();

    // Verify ownership
    if (commentData.authorAddress !== normalizedUserIdentifier) {
      throw new Error('Not authorized to delete this comment');
    }

    await updateDoc(commentRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });

    // If this is a reply, decrement thread root's reply count (for total thread count)
    if (commentData.threadRootId) {
      const threadRootRef = doc(db, 'comments', commentData.threadRootId);
      await updateDoc(threadRootRef, {
        replyCount: increment(-1)
      });
    }

    return true;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

// ==================== VOTING OPERATIONS ====================

// Vote on a comment
export const voteComment = async (commentDocId, userIdentifier, voteType) => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('You must be signed in to vote on a comment');
    }

    // Use auth.currentUser.uid consistently for all operations
    // For wallet users: uid = address (e.g., "0x1234...")
    // For email users: uid = "e_xxx"
    const uid = auth.currentUser.uid;
    const voteId = `${uid}_${commentDocId}`;
    const voteRef = doc(db, 'commentVotes', voteId);
    const voteSnap = await getDoc(voteRef);

    const commentRef = doc(db, 'comments', commentDocId);
    const commentSnap = await getDoc(commentRef);

    if (!commentSnap.exists()) {
      throw new Error('Comment not found');
    }

    // For upvoters/downvoters arrays, use uid (already normalized for wallet users)
    const isEmailUser = isEmailUserIdentifier(uid);
    const userAddr = isEmailUser ? uid : uid.toLowerCase();

    if (voteSnap.exists()) {
      // User already voted - change vote or remove
      const existingVote = voteSnap.data();

      if (existingVote.voteType === voteType) {
        // Remove vote (clicked same button)
        await deleteDoc(voteRef);

        // Update comment
        const updateData = {};
        if (voteType === 'upvote') {
          updateData.upvotes = increment(-1);
          updateData.upvoters = arrayRemove(userAddr);
        } else {
          updateData.downvotes = increment(-1);
          updateData.downvoters = arrayRemove(userAddr);
        }
        await updateDoc(commentRef, updateData);

        return { action: 'removed', voteType };
      } else {
        // Change vote
        await updateDoc(voteRef, {
          voteType,
          createdAt: serverTimestamp()
        });

        // Update comment (remove old, add new)
        const updateData = {};
        if (voteType === 'upvote') {
          updateData.upvotes = increment(1);
          updateData.downvotes = increment(-1);
          updateData.upvoters = arrayUnion(userAddr);
          updateData.downvoters = arrayRemove(userAddr);
        } else {
          updateData.upvotes = increment(-1);
          updateData.downvotes = increment(1);
          updateData.upvoters = arrayRemove(userAddr);
          updateData.downvoters = arrayUnion(userAddr);
        }
        await updateDoc(commentRef, updateData);

        return { action: 'changed', voteType };
      }
    } else {
      // New vote
      // Document ID contains all necessary info: {compositeId}_{commentId}
      // No need to duplicate commentId and voterAddress in fields
      await setDoc(voteRef, {
        voteType,
        createdAt: serverTimestamp()
      });

      // Update comment
      const updateData = {};
      if (voteType === 'upvote') {
        updateData.upvotes = increment(1);
        updateData.upvoters = arrayUnion(userAddr);
      } else {
        updateData.downvotes = increment(1);
        updateData.downvoters = arrayUnion(userAddr);
      }
      await updateDoc(commentRef, updateData);

      return { action: 'added', voteType };
    }
  } catch (error) {
    console.error('Error voting on comment:', error);
    throw error;
  }
};

// Get user's vote on a comment
export const getUserVote = async (commentDocId, userAddress) => {
  try {
    // Use composite ID from auth if available (format: "ethereum_0x...")
    if (!auth.currentUser) {
      return null; // Not authenticated, no vote
    }

    const compositeId = auth.currentUser.uid;
    const voteId = `${compositeId}_${commentDocId}`;
    const voteSnap = await getDoc(doc(db, 'commentVotes', voteId));

    if (voteSnap.exists()) {
      return voteSnap.data().voteType;
    }
    return null;
  } catch (error) {
    console.error('Error getting user vote:', error);
    return null;
  }
};

// Get user's votes on multiple comments in batch (optimized)
export const getUserVotesBatch = async (commentDocIds, userAddress) => {
  try {
    if (!commentDocIds || commentDocIds.length === 0) {
      return {};
    }

    // Use composite ID from auth if available (format: "ethereum_0x...")
    if (!auth.currentUser) {
      return {}; // Not authenticated, no votes
    }

    const compositeId = auth.currentUser.uid;

    // Fetch all votes in parallel
    const votePromises = commentDocIds.map(commentId => {
      const voteId = `${compositeId}_${commentId}`;
      return getDoc(doc(db, 'commentVotes', voteId));
    });

    const voteSnaps = await Promise.all(votePromises);

    // Build result map
    const votesMap = {};
    voteSnaps.forEach((snap, index) => {
      if (snap.exists()) {
        votesMap[commentDocIds[index]] = snap.data().voteType;
      }
    });

    return votesMap;
  } catch (error) {
    console.error('Error getting user votes batch:', error);
    return {};
  }
};

// ==================== FLAG/REPORT OPERATIONS ====================

// Flag a comment
export const flagComment = async (commentDocId, userIdentifier, reason, details = '') => {
  try {
    // Check authentication
    if (!auth.currentUser) {
      throw new Error('You must be signed in to flag a comment');
    }

    // Document ID format: {compositeId}_{commentId}
    // Store commentId as field for easier querying in admin tools
    const flagData = {
      commentId: commentDocId,
      reason,
      details,
      createdAt: serverTimestamp(),
      resolved: false,
      resolvedAt: null,
      resolvedBy: null,
      action: null
    };

    // Use composite ID from auth (format: "ethereum_0x..." or "e_xxx")
    const compositeId = auth.currentUser.uid;
    const flagId = `${compositeId}_${commentDocId}`;
    await setDoc(doc(db, 'commentFlags', flagId), flagData);

    // Update comment flag count
    const commentRef = doc(db, 'comments', commentDocId);
    const commentSnap = await getDoc(commentRef);

    // For email users, use compositeId; for wallet users, use lowercased address
    const isEmailUser = isEmailUserIdentifier(userIdentifier);
    const userAddr = isEmailUser ? userIdentifier : userIdentifier.toLowerCase();

    if (commentSnap.exists()) {
      const newFlagCount = (commentSnap.data().flagCount || 0) + 1;
      const updateData = {
        flagCount: increment(1),
        flaggedBy: arrayUnion(userAddr)
      };

      // Auto-moderate based on flag count
      if (newFlagCount >= 5) {
        updateData.flagged = true;
      }
      if (newFlagCount >= 10) {
        updateData.removed = true;
        updateData.removedAt = serverTimestamp();
        updateData.removedBy = 'auto-moderation';
      }

      await updateDoc(commentRef, updateData);
    }

    return true;
  } catch (error) {
    console.error('Error flagging comment:', error);
    throw error;
  }
};

// ==================== LATEST COMMENTS ====================

/**
 * Get the latest comments across all addresses and coins
 * Used for displaying recent activity on homepage/search areas
 */
export const getLatestComments = async (limitCount = 6) => {
  try {
    const q = query(
      collection(db, 'comments'),
      where('removed', '==', false),
      where('deleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching latest comments:', error);
    throw error;
  }
};

// ==================== UTILITY FUNCTIONS ====================
